import * as React from 'react';
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { Image, Icon, mergeStyleSets, MessageBar, MessageBarType, Theme, Spinner, SpinnerSize, Dropdown, IDropdownOption } from '@fluentui/react';
import { createRef, useState } from 'react';
import { People, Person, PersonCardInteraction, PersonViewType } from "@microsoft/mgt-react";
import { Providers } from '@microsoft/mgt-element';
import { Panel } from '@fluentui/react/lib/Panel';
import { useBoolean } from '@fluentui/react-hooks';
import { useTranslation } from 'react-i18next';
import { SubsUsersTable } from './SubsUsersTable';
import { AppObject } from './AppCard';
import { callFunction, loadContactData } from '../lib/Helpers';
import "../styles/Settings_Subscriptions.css"
import { panelDarkTheme } from '../App';

type SubscriptionCardProps = {
  subscription?: SubscriptionObject,
  apps?: AppObject[],
  currentUser: string,
  tid: string,
  theme?: Theme,
  themeName?: string,
  onReloadRequest: React.Dispatch<void>,
};

export type EntitlementsObject = {
  appid: string,
  type: string,
  unit: string,
  value: string,
}

export type PlanObject = {
  planId: string,
  displayName: string,
  isPrivate: boolean,
  entitlements: EntitlementsObject[],
}

export type TermObject = {
  startDate: string,
  endDate: string,
  termUnit: string,
}

export type SubscriptionObject = {
  id: string,
  name: string,
  img: string,
  publisherId: string,
  offerId?: string,
  planId: PlanObject,
  quantity: string,
  beneficiary: string,
  isFreeTrial: boolean,
  isTest: boolean,
  saasSubscriptionStatus: string,
  term: TermObject,
  adminUsers: string[],
  users: string[]
}

const azureRegions: IDropdownOption[] = [
  { key: 'eastus', text: 'East US' },
  { key: 'eastus2', text: 'East US 2' },
  { key: 'westus', text: 'West US' },
  { key: 'westus2', text: 'West US 2' },
  { key: 'westus3', text: 'West US 3' },
  { key: 'centralus', text: 'Central US' },
  { key: 'northcentralus', text: 'North Central US' },
  { key: 'southcentralus', text: 'South Central US' },
  { key: 'westcentralus', text: 'West Central US' },
  { key: 'canadacentral', text: 'Canada Central' },
  { key: 'canadaeast', text: 'Canada East' },
  { key: 'brazilsouth', text: 'Brazil South' },
  { key: 'northeurope', text: 'North Europe' },
  { key: 'westeurope', text: 'West Europe' },
  { key: 'uksouth', text: 'UK South' },
  { key: 'ukwest', text: 'UK West' },
  { key: 'francecentral', text: 'France Central' },
  { key: 'germanywestcentral', text: 'Germany West Central' },
  { key: 'switzerlandnorth', text: 'Switzerland North' },
  { key: 'norwayeast', text: 'Norway East' },
  { key: 'swedencentral', text: 'Sweden Central' },
  { key: 'eastasia', text: 'East Asia' },
  { key: 'southeastasia', text: 'Southeast Asia' },
  { key: 'japaneast', text: 'Japan East' },
  { key: 'japanwest', text: 'Japan West' },
  { key: 'australiaeast', text: 'Australia East' },
  { key: 'australiasoutheast', text: 'Australia Southeast' },
  { key: 'koreacentral', text: 'Korea Central' },
  { key: 'centralindia', text: 'Central India' },
  { key: 'southindia', text: 'South India' },
  { key: 'southafricanorth', text: 'South Africa North' },
  { key: 'uaenorth', text: 'UAE North' },
];

const SubscriptionCard: React.FC<SubscriptionCardProps> = ({ subscription, apps, currentUser, tid, theme, themeName, onReloadRequest }) => {
  const appInsights = useAppInsightsContext();
  const { t } = useTranslation();
  const cardContainer = mergeStyleSets({
    backgroundColor: theme?.palette.neutralLighter,
    borderTop: '3px solid #FD6262',
    borderRadius: '8px',
    width: '100%',
    overflow: 'hidden',
    boxShadow: themeName === 'bitDarkTheme' ? '0 2px 8px rgba(0,0,0,0.4)' : '0 2px 8px rgba(0,0,0,0.08)',
  });

  const [savedAdmins, setSavedAdmins] = useState<string[]>(subscription?.adminUsers as string[]);
  const [savedUsers, setSavedUsers] = useState<string[]>(subscription?.users as string[]);
  const [admins, setAdmins] = useState<string[]>(subscription?.adminUsers as string[]);
  const [users, setUsers] = useState<string[]>(subscription?.users as string[]);
  const [, setNotifyUsers] = useState<string[]>([]);
  const userPeopleRef = createRef();

  const userIsAdmin: boolean = (subscription?.adminUsers.find((value) => value === currentUser) ? true : false)
  const userIsOwner: boolean = (subscription?.beneficiary === currentUser ? true : false)

  const onPanelCloseClicked = () => {
    setAdmins(savedAdmins);
    setUsers(savedUsers);
    dismissPanel();
  }

  const [isOpen, { setTrue: openPanel, setFalse: dismissPanel }] = useBoolean(false);
  const [savingError, setSavingError] = useState<string | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isRequestingAccess, setIsRequestingAccess] = useState(false);

  // The permission to list/search directory users (User.ReadBasic.All) is not
  // requested at login. It is requested incrementally, only when an admin opens
  // the panel to assign licenses, so a normal sign-in stays minimal. Note this is
  // a user-consentable scope (no admin approval required).
  const onManageClicked = () => {
    setIsRequestingAccess(true);
    Providers.globalProvider.getAccessTokenForScopes("User.ReadBasic.All")
      .then(() => { openPanel(); })
      .catch((error) => { if (appInsights.isInitialized()) appInsights.trackException({ exception: error, properties: { component: 'SubscriptionCard', location: 'onRequestUserReadBasicAll' } }); })
      .finally(() => { setIsRequestingAccess(false); });
  };

  const onRenderFooterContent = React.useCallback(
    () => {
      const onSaveClicked = () => {

        if (isDirty) {
          setIsSaving(true);
          loadContactData(users).then((conts) => {
            const data: any =
            {
              subid: subscription?.id,
              updateType: "multiple",
              users: users,
              adminUsers: admins,
              contacts: conts.map((value) => {
                return (
                  {
                    name: value.displayName,
                    emailId: value.mail,
                    objectId: value.id,
                    tenantId: tid
                  })
              })

            }
            callFunction(true, "patchSubscriptionUsers?{0}", JSON.stringify(data))
              .then((data) => {
                onReloadRequest();
                setSavedAdmins(admins);
                setSavedUsers(users);
                setSavingError(undefined);
                setIsSaving(false);
                dismissPanel();
              })
              .catch((error) => { if (appInsights.isInitialized()) appInsights.trackException({ exception: error, properties: { component: 'SubscriptionCard', location: 'onPatchSubscriptionUsers' } }); setSavingError(error); setIsSaving(false); dismissPanel(); });
          })
            .catch((error) => { if (appInsights.isInitialized()) appInsights.trackException({ exception: error, properties: { component: 'SubscriptionCard', location: 'onLoadContactData' } }); });
        }
        else {
          dismissPanel();
        }
      }

      const onCancelClicked = () => {
        setAdmins(savedAdmins);
        setUsers(savedUsers);
        dismissPanel();
      }

      return (
        <div>
          <div style={{ display: 'flex' }}>
            <button onClick={onSaveClicked} className="bitbutton" >{t('save')}</button>
            {isSaving && (<Spinner size={SpinnerSize.small} />)}
            <button onClick={onCancelClicked} className="bitbuttoncancel">{t('cancel')}</button>
          </div>
        </div>)
    },
    [admins, appInsights, dismissPanel, isDirty, isSaving, onReloadRequest, savedAdmins, savedUsers, subscription?.id, t, tid, users],
  );

  const [isActivating, setIsActivating] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState<string | undefined>(undefined);
  function activateSubscription(subid: string, planid: string, quantity: string, region?: string) {
    setIsActivating(true);
    let url = "activateSubscription?{0}&subid=" + subid + "&planid=" + planid + "&quantity=" + quantity;
    if (region) url += "&region=" + encodeURIComponent(region);
    callFunction(true, url)
      .catch((error) => { alert(t('activateError')); if (appInsights) appInsights.trackException({ exception: error, properties: { component: 'App', location: 'onActivateSubscription' } }); })
      .then(() => { onReloadRequest(); })
      .finally(() => { setIsActivating(false); })

  }

  const isDark = themeName === 'bitDarkTheme';
  const isActive = subscription?.saasSubscriptionStatus.trim() === "Subscribed";
  const isPending = subscription?.saasSubscriptionStatus.trim() === "PendingFulfillmentStart";
  const requiresRegion = subscription?.offerId === '21719032-056d-49f6-bc85-4c469fc26c25' || subscription?.offerId === '21719032-056d-49f6-bc85-4c469fc26c25-preview';
  const assignedCount = subscription?.users.length ?? 0;
  const totalCount = parseInt(subscription?.quantity ?? '0');
  const licensePct = totalCount > 0 ? Math.round((assignedCount / totalCount) * 100) : 0;

  const sectionLabel: React.CSSProperties = {
    fontWeight: 600,
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
    color: '#FD6262',
    marginBottom: '8px',
    marginTop: '2px',
    display: 'block',
  };

  const sectionRow: React.CSSProperties = {
    padding: '14px 16px',
    borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)'}`,
  };

  return (
    <div style={{ margin: '0 0 16px 0', width: '100%' }}>
      {subscription?.isFreeTrial && (
        <MessageBar messageBarType={MessageBarType.info} isMultiline={false} styles={{ root: { borderRadius: '6px 6px 0 0', marginBottom: 0 } }}>
          {t('trialNote')}
        </MessageBar>
      )}

      {/* ── Card ── */}
      <div aria-roledescription="Subscription card" style={cardContainer} key={subscription?.id}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px 10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Image style={{ height: '40px', width: '40px', objectFit: 'contain', borderRadius: '6px' }} src={subscription?.img} />
            <div>
              <div style={{ fontWeight: 700, fontSize: '15px' }}>{subscription?.planId.displayName}</div>
              <div style={{ fontSize: '12px', opacity: 0.65 }}>{subscription?.name}</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px', opacity: 0.6 }}>{t('expires')}</div>
              <div style={{ fontSize: '13px', fontWeight: 500 }}>{subscription?.term.endDate || '—'}</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
              <div style={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px', opacity: 0.6 }}>{t('owner')}</div>
              <Person className={isDark ? 'mgt-dark' : ''} userId={subscription?.beneficiary} view={PersonViewType.avatar} personCardInteraction={PersonCardInteraction.none} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
              <div style={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px', opacity: 0.6 }}>{t('status')}</div>
              {isActive
                ? <span style={{ background: '#107c10', color: '#fff', padding: '3px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: 600 }}>{t('active')}</span>
                : <span style={{ background: '#d83b01', color: '#fff', padding: '3px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: 600 }}>{t('pending')}</span>
              }
              {isPending && userIsOwner && requiresRegion && (
                <div style={{ marginTop: '6px', width: '100%', textAlign: 'left' }}>
                  <div style={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px', opacity: 0.6, marginBottom: '4px' }}>{t('azureRegion')}</div>
                  <Dropdown
                    placeholder={t('selectRegion')}
                    options={azureRegions}
                    selectedKey={selectedRegion ?? null}
                    onChange={(_, opt) => setSelectedRegion(opt?.key as string)}
                    styles={{ root: { minWidth: '180px' } }}
                  />
                </div>
              )}
              {isPending && (
                <button hidden={!userIsOwner} disabled={isActivating || (requiresRegion && !selectedRegion)} className='bitbuttonsmall' style={{ marginTop: '4px', minWidth: '80px' }}
                  onClick={() => { appInsights.trackEvent({ name: "Activate Clicked", properties: { subsName: subscription?.id } }); activateSubscription(subscription?.id, subscription?.planId.planId, subscription?.quantity, requiresRegion ? selectedRegion : undefined); }}>
                  {isActivating && (<Spinner size={SpinnerSize.small} />)}{isActivating ? ` ${t('activating')}` : t('activate')}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* AI solution note */}
        {(subscription?.offerId === '6158e422-db1b-47bb-9970-e331d5faa8de' || subscription?.offerId === '6158e422-db1b-47bb-9970-e331d5faa8de-preview' || subscription?.offerId === '0cc99633-fc4d-421a-93e2-13187e574186' || subscription?.offerId === '0cc99633-fc4d-421a-93e2-13187e574186-preview') && (
          <div style={{ margin: '0 16px 4px', borderRadius: '6px', background: isDark ? 'rgba(253,98,98,0.12)' : 'rgba(253,98,98,0.08)', padding: '10px 14px', fontSize: '13px', borderLeft: '3px solid #FD6262' }}>
            {t('aiNote')}{' '}
            <a href="https://ai.bitfractal.com/" target="_blank" rel="noreferrer" style={{ color: '#FD6262', fontWeight: 600 }}>
              {t('aiSignIn')}
            </a>{' '}
            {t('aiCredentials')}
          </div>
        )}

        {savingError && (
          <div style={{ margin: '0 16px 4px' }}>
            <MessageBar messageBarType={MessageBarType.error} isMultiline={false}>
              {t('savingError') + savingError}
            </MessageBar>
          </div>
        )}

        {/* Apps & Entitlements */}
        <div style={sectionRow}>
          <span style={sectionLabel}>{t('appsEntitlements')}</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {subscription?.planId.entitlements.map((ent) => {
              const app = apps?.find((a) => a.id === ent.appid);
              return (
                <div key={ent.appid + ent.type} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 12px', borderRadius: '6px', background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}` }}>
                  {app && <Icon iconName={app.iconName} style={{ color: '#FD6262', fontSize: '16px' }} />}
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '13px' }}>{app?.name ?? ent.appid}</div>
                    <div style={{ fontSize: '11px', opacity: 0.65 }}>{ent.type}: {ent.value}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Users */}
        <div style={sectionRow}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={sectionLabel}>{t('users')}</span>
            <button hidden={!userIsAdmin || subscription?.isFreeTrial} disabled={isRequestingAccess} className='bitbuttonsmall' onClick={onManageClicked}>{isRequestingAccess && (<Spinner size={SpinnerSize.small} />)}{t('manage')}</button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div>
              <div style={{ fontSize: '11px', fontWeight: 600, opacity: 0.6, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('members')}</div>
              <People className={isDark ? 'mgt-dark' : ''} ref={userPeopleRef} showMax={25} userIds={users} personCardInteraction={PersonCardInteraction.none} />
            </div>
            <div>
              <div style={{ fontSize: '11px', fontWeight: 600, opacity: 0.6, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('admins')}</div>
              <People className={isDark ? 'mgt-dark' : ''} showMax={25} userIds={admins} personCardInteraction={PersonCardInteraction.none} />
            </div>
          </div>
        </div>

        {/* Licenses */}
        <div style={sectionRow}>
          <span style={sectionLabel}>{t('licenses')}</span>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
            <span style={{ fontSize: '13px' }}><strong>{assignedCount}</strong> {t('of')} <strong>{totalCount}</strong> {t('assigned')}</span>
            {(userIsOwner && !subscription?.isFreeTrial) && (
              <span style={{ fontSize: '11px', opacity: 0.6 }}>{t('licenseNote')}</span>
            )}
          </div>
          {/* Progress bar */}
          <div style={{ height: '6px', borderRadius: '3px', background: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${licensePct}%`, background: licensePct > 90 ? '#d83b01' : '#FD6262', borderRadius: '3px', transition: 'width 0.4s ease' }} />
          </div>
        </div>

      </div>

      <Panel
        isOpen={isOpen}
        theme={isDark ? panelDarkTheme : theme}
        onDismiss={onPanelCloseClicked}
        headerText={t('manageUsers')}
        closeButtonAriaLabel="Close"
        onRenderFooterContent={onRenderFooterContent}
        isFooterAtBottom={true}>
        <SubsUsersTable users={users as string[]} admins={admins as string[]} owner={subscription?.beneficiary as string} maxUsers={+(subscription?.quantity as string)} theme={theme} themeName={themeName} onAdminsChanged={setAdmins} onUsersChanged={setUsers} onIsDirty={setIsDirty} onToNotifyUsersChanged={setNotifyUsers} />
      </Panel>
    </div>
  );
};

export default SubscriptionCard;
